import { Capacitor } from "@capacitor/core";
import { isAxiosError } from "axios";
import { motion } from "framer-motion";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { LoaderFunction, useNavigate, useSearchParams } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { AccountSetupLoading } from "@/modules/onboarding/components/AccountSetupLoading/AccountSetupLoading";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import OnboardingHeaderV3 from "@/modules/onboarding/components/OnboardingScreenHeader/OnboardingHeaderV3";
import { QRScanner } from "@/organisms/qr-scanner/QRScanner";
import { get } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

import { useAccountSetup } from "./utils/accountSetup";

const load = async () => {
  // We still load the platform tokens to maintain session state if needed
  let platformDomainTokens: Models<"GetMultiPlatformUserPlatformResult">[] = [];
  try {
    platformDomainTokens = await queryClient
      .fetchQuery({
        queryKey: ["/multi-platform/list"],
        queryFn: () => get("/multi-platform/list"),
      })
      .then((res) => res.platformDomainTokens);
  } catch {
    // If the list fails to load, leave it empty
  }

  return waitObj({ platformDomainTokens });
};

export const v3PlatformPickerLoader: LoaderFunction = () => {
  return load();
};

const getPlatformIdFromScannedCodeDebounced = debounce(
  (scannedCode: string) =>
    get("/multi-platform/platform/qrcode", { scannedCode }),
  500,
  { leading: true, trailing: false }
);

const V3PlatformPicker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading: isAccountSetupLoading, error: accountSetupError } =
    useAccountSetup();
  const referralCode = searchParams.get("referralCode");

  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isQrScanInProgress, setIsQrScanInProgress] = useState(false);

  // Store referral code in localStorage when present
  useEffect(() => {
    if (referralCode) {
      localStorage.setItem("referralCode", referralCode);
    }
  }, [referralCode]);

  const handleSearchForPractice = () => {
    const referralParam = referralCode ? `?referralCode=${referralCode}` : "";
    navigate(`/signin/location${referralParam}`, { replace: true });
  };

  const handleScanError = (error: Error) => {
    const errorStatus = isAxiosError(error) ? error.response?.status : null;
    if (errorStatus === 400 || errorStatus === 401) {
      setErrorMessage("Unrecognized QR code - please try again");
    } else {
      setErrorMessage(
        "Something went wrong - please contact support if this issue persists"
      );
    }
    setShowErrorMessage(true);
  };

  // QR Scanner success handling
  const handleScannedCode = useCallback(
    async (scannedCode: string) => {
      if (isAccountSetupLoading) {
        return;
      }

      try {
        const { platformId, platformLocationId } =
          await getPlatformIdFromScannedCodeDebounced(scannedCode);
        if (!platformId || !platformLocationId) {
          return;
        }
        setShowQRScanner(false);

        // Store platform and location info
        localStorage.setItem("platformId", platformId);
        localStorage.setItem("fromQrScan", "true"); // Mark as QR scan flow
        localStorage.removeItem("locationConfirmed");

        // Navigate to location picker with preselected location
        const referralParam = referralCode
          ? `&referralCode=${referralCode}`
          : "";
        navigate(
          `/signin/location?platformId=${platformId}&locationId=${platformLocationId}${referralParam}`,
          { replace: true }
        );
      } catch (error) {
        handleScanError(error as Error);
        setIsQrScanInProgress(false);
      }
    },
    [isAccountSetupLoading, navigate, referralCode]
  );

  // Handle account setup errors
  useEffect(() => {
    if (accountSetupError) {
      setErrorMessage("Account setup failed - please try again");
      setShowErrorMessage(true);
      setIsQrScanInProgress(false);
    }
  }, [accountSetupError]);

  useEffect(() => {
    if (showErrorMessage) {
      const timeout = setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [showErrorMessage]);

  return (
    <>
      {showQRScanner && (
        <QRScanner
          errorMessage={showErrorMessage ? errorMessage : undefined}
          onClose={() => {
            setShowQRScanner(false);
            setShowErrorMessage(false);
            setErrorMessage("");
          }}
          onResult={handleScannedCode}
        />
      )}

      <MobileViewportContainer header={<OnboardingBackButton prev="/signin" />}>
        {isAccountSetupLoading || isQrScanInProgress ? (
          <AccountSetupLoading />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
            className="flex w-full max-w-sm flex-col items-center gap-9"
          >
            {/* Header Section */}
            <OnboardingHeaderV3
              title="Let's get you connected."
              subtitle={
                <>
                  <p className="mb-1.5">
                    Search for a practice near you to access exclusive rewards
                    and offers.
                  </p>
                  <p>
                    Already in one of our practices?
                    <br />
                    Just scan the QR code to get started.
                  </p>
                </>
              }
              align="left"
            />

            {/* Buttons Section */}
            <div className="flex w-full flex-col gap-4">
              <Button
                size="large"
                style="primary"
                fullWidth
                onClick={handleSearchForPractice}
              >
                Search for a practice
              </Button>

              {Capacitor.isNativePlatform() && (
                <Button
                  size="large"
                  style="secondary"
                  fullWidth
                  onClick={() => setShowQRScanner(true)}
                >
                  Scan QR Code in practice
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </MobileViewportContainer>
    </>
  );
};

export default V3PlatformPicker;
